import { configureStore } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import subscriptions from "./subscriptions/slice";
import images from "./images/slice";
import halls from "./halls/slice";

import changePass from "./profile/settings/changePass/slice";
import rent from "./rent/slice";
import cart from "./cart/slice";
import authAndReg from "./profile/authAndReg/slice";
import getToken from "./profile/paytoken//slice";
import getCards from "./profile/cards/slice";
import getHistory from "./profile/history/slice";
import getProfile from "./profile/settings/getProfileInfo/slice";
import changeName from "./profile/settings/changeDataProfile/slice";
import addCard from "./profile/cards//slice";
import rerender from "./rerender/slice";
import feedback from "./feedback/slice";
import school from "./school/slice";
import news from "./News/slice";
import contacts from "./contacts/slice";
import club from "./club/slice";
import notices from "./notices/slice";
import getHalls from "./profile/hallsHistory/slice";
import purchaseHistory from "./profile/purchaseHistory/slice";
import getProfileInfo from "./profile/profileInfo/slice";
import authReducer from "./auth/slice";
import userReducer from "./user/slice";
import tempProfiles from "./profile/tempProfiles/slice";
import basketTemp from "./cart/basketTemp/slice";
import { IAuthState } from "./auth/types";
import { IUserState } from "./user/types";
import { ICartState } from "./cart/types";
import { IAuthAndReg } from "./profile/authAndReg/types";

const authPersistConfig = {
  key: "auth",
  storage: storage,
  whitelist: ["accessToken", "phone", "authData"],
};

const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);

const userPersistConfig = {
  key: "user",
  storage: storage,
  whitelist: ["user", "gift", "partnerId"],
};

const persistedUserReducer = persistReducer(userPersistConfig, userReducer);

const cartPersistConfig = {
  key: "cart",
  storage: storage,
  whitelist: ["basket", "acceptedNotices"],
};

const persistedCartReducer = persistReducer(cartPersistConfig, cart);

const authAndRegPersistConfig = {
  key: "authAndReg",
  storage: storage,
  whitelist: ["auth"],
};

const persistedAuthAndRegReducer = persistReducer(authAndRegPersistConfig, authAndReg);

const store = configureStore({
  reducer: {
    subscriptions,
    images,
    halls,

    rerender,
    authAndReg: persistedAuthAndRegReducer,
    rent,
    cart: persistedCartReducer,
    getCards,
    getHistory,
    getProfile,
    changeName,
    changePass,
    addCard,
    getToken,
    news,
    feedback,
    school,
    getHalls,
    purchaseHistory,
    getProfileInfo,
    contacts,
    club,
    notices,
    auth: persistedAuthReducer,
    user: persistedUserReducer,
    tempProfiles,
    basketTemp,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    }),
  devTools: typeof process !== "undefined" && process.env 
    ? process.env.NODE_ENV !== "production" 
    : true,
});

type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();

export type RootState = Omit<ReturnType<typeof store.getState>, 'cart' | 'auth' | 'user' | 'authAndReg'> & {
  cart: ICartState;
  auth: IAuthState;
  user: IUserState;
  authAndReg: IAuthAndReg;
  changePass: ReturnType<typeof store.getState>['changePass'];
  tempProfiles: ReturnType<typeof store.getState>['tempProfiles'];
  basketTemp: ReturnType<typeof store.getState>['basketTemp'];
};

export const persistor = persistStore(store);

export default store;
